import PropTypes from 'prop-types'
import { validarEAN as validarEANService } from '../../servicios/publicacionesService'
import styles from './InputEAN.module.css'

export const GTIN_KEY = 'GTIN'
export const MAX_LENGTH_EAN = 13

const LONGITUD_EAN8 = 8
const LONGITUD_EAN13 = 13
const BASE_VERIFICADOR = 10
const MULTIPLICADOR_PAR_EAN13 = 1
const MULTIPLICADOR_IMPAR_EAN13 = 3
const MULTIPLICADOR_PAR_EAN8 = 3
const MULTIPLICADOR_IMPAR_EAN8 = 1
const MODULO_PARIDAD = 2

export const MENSAJE_EAN_INVALIDO =
  'El código EAN ingresado no es válido. Debe ser un EAN-8 o EAN-13 correcto.'

/**
 * Valida el dígito verificador de un EAN-8 / EAN-13.
 * @param {string} ean
 * @returns {boolean}
 */
export const validarFormatoEAN = (ean) => {
  if (!ean) return false

  const str = String(ean).trim()

  if (str.length !== LONGITUD_EAN8 && str.length !== LONGITUD_EAN13) return false
  if (!/^\d+$/.test(str)) return false

  const digitos = str.split('').map(Number)
  const chequeaDigitos = digitos.pop()

  const isEAN13 = str.length === LONGITUD_EAN13
  const multiplicadorPar = isEAN13 ? MULTIPLICADOR_PAR_EAN13 : MULTIPLICADOR_PAR_EAN8
  const multiplicadorImpar = isEAN13 ? MULTIPLICADOR_IMPAR_EAN13 : MULTIPLICADOR_IMPAR_EAN8

  let suma = 0
  digitos.forEach((digito, index) => {
    const multiplicador = index % MODULO_PARIDAD === 0 ? multiplicadorPar : multiplicadorImpar
    suma += digito * multiplicador
  })

  const calculado = (BASE_VERIFICADOR - (suma % BASE_VERIFICADOR)) % BASE_VERIFICADOR
  return calculado === chequeaDigitos
}

/**
 * Valida el formato de un EAN y devuelve el mensaje de error, o cadena vacía si es válido.
 * Un valor vacío se considera válido (el EAN es opcional).
 * @param {string} valor
 * @returns {string}
 */
export const validarMensajeEAN = (valor) => {
  const normalizado = valor?.trim() || ''
  if (normalizado && !validarFormatoEAN(normalizado)) {
    return MENSAJE_EAN_INVALIDO
  }
  return ''
}

/**
 * Chequea contra el backend si un EAN ya está publicado por el vendedor.
 *
 * - Si el EAN está vacío, no consulta y devuelve `null` (el EAN es opcional).
 * - Si `idActual` se provee, ignora coincidencias con la propia publicación
 *   (caso Administrar). Omitirlo aplica al alta, donde aún no hay id (caso Publicar).
 *
 * @param {string} ean
 * @param {number} idVendedor
 * @param {number} [idActual] - idPublicacion en edición, para excluirla.
 * @returns {Promise<{ idPublicacion: number, titulo: string } | null>}
 */
export const chequearEanDuplicado = async (ean, idVendedor, idActual) => {
  if (!ean) {
    return null
  }
  const { data } = await validarEANService(ean, idVendedor)
  if (data?.duplicado && data.idPublicacion !== idActual) {
    return data
  }
  return null
}

/**
 * Mensaje reutilizable de EAN duplicado, con link a la publicación existente.
 * @param {{ idPublicacion: number, titulo: string }} data - Publicación duplicada.
 * @param {{ conId?: boolean }} [opciones] - Si `conId` es true, antepone "#id — " al título.
 * @returns {import('react').ReactNode}
 */
export const mensajeEanDuplicado = (data, { conId = false } = {}) => (
  <>
    Ya tenés este producto publicado:{' '}
    <a href={`/Publicacion/${data.idPublicacion}`} target="_blank" rel="noreferrer">
      {conId ? `#${data.idPublicacion} — ${data.titulo}` : data.titulo}
    </a>
  </>
)

/**
 * Input reutilizable para el código EAN (GTIN) de una publicación.
 *
 * Centraliza el markup del input (name/maxLength/inputMode), la validación de
 * formato EAN-8/EAN-13, el chequeo de duplicado y la presentación del error,
 * para no duplicar la lógica entre Publicar y Administrar Publicación.
 *
 * @param {string} value - Valor actual del EAN (controlado por el padre).
 * @param {(valor: string) => void} onChange - Recibe el nuevo valor del input.
 * @param {(resultado: { valor: string, errorFormato: string, duplicada: object | null }) => void} [onBlur] - Se invoca al perder el foco con el resultado de validar formato y (si corresponde) duplicado.
 * @param {number} [idVendedor] - Si se provee, el input chequea duplicado contra backend en el blur.
 * @param {number} [idActual] - idPublicacion en edición, para excluirla del chequeo de duplicado.
 * @param {() => void} [onCheckError] - Se invoca si falla la consulta de duplicado al backend.
 * @param {(promesa: Promise<void>) => void} [onValidatingChange] - Recibe la promesa del chequeo en curso, para que el contenedor pueda esperarla antes de leer el error.
 * @param {import('react').ReactNode} [error] - Error a mostrar debajo del input; también lo pinta en estado inválido.
 * @param {boolean} [invalid] - Pinta el input como inválido sin renderizar texto.
 * @param {string} [label] - Etiqueta a mostrar junto al input (variante "administrar").
 * @param {import('react').ReactNode} [descripcion] - Texto de ayuda a mostrar sobre el input (variante "publicar").
 * @param {'publicar' | 'administrar'} [variant] - Ajusta el estilo según el contexto.
 * @param {string} [placeholder]
 */
const InputEAN = ({
  value,
  onChange,
  onBlur,
  idVendedor,
  idActual,
  onCheckError,
  onValidatingChange,
  error = '',
  invalid = false,
  label = 'Código EAN (código de barras):',
  descripcion,
  variant = 'publicar',
  placeholder = 'Opcional. Ej: 7791234567890'
}) => {
  const tieneErrorInline = Boolean(error)
  const esInvalido = tieneErrorInline || Boolean(invalid)

  const handleChange = (e) => {
    onChange(e.target.value)
  }

  const handleBlur = async () => {
    if (!onBlur) {
      return
    }
    const valor = value?.trim() || ''
    const errorFormato = validarMensajeEAN(valor)

    const chequeaDuplicado = idVendedor !== null && idVendedor !== undefined
    if (errorFormato || !valor || !chequeaDuplicado) {
      onBlur({ valor, errorFormato, duplicada: null })
      return
    }

    const promesaChequeo = (async () => {
      try {
        const duplicada = await chequearEanDuplicado(valor, idVendedor, idActual)
        onBlur({ valor, errorFormato: '', duplicada })
      } catch {
        if (onCheckError) {
          onCheckError()
        }
        onBlur({ valor, errorFormato: '', duplicada: null })
      }
    })()

    if (onValidatingChange) {
      onValidatingChange(promesaChequeo)
    }
    await promesaChequeo
  }

  const clasesInput =
    variant === 'administrar'
      ? `${styles.input}${esInvalido ? ' error' : ''}`
      : `titulo-publicacion${esInvalido ? ' error' : ''}`

  const input = (
    <input
      type="text"
      name="ean"
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={clasesInput}
      maxLength={MAX_LENGTH_EAN}
      inputMode="numeric"
    />
  )

  if (variant === 'administrar') {
    return (
      <div className={styles.campoGtin}>
        <span className={styles.etiqueta}>{label}</span>
        <div className={styles.inputWrapper}>{input}</div>
        {tieneErrorInline && <span className={styles.errorGtin}>{error}</span>}
      </div>
    )
  }

  return (
    <>
      {descripcion && <p className="descripcion-cuadro-publicacion">{descripcion}</p>}
      {input}
    </>
  )
}

InputEAN.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  idVendedor: PropTypes.number,
  idActual: PropTypes.number,
  onCheckError: PropTypes.func,
  onValidatingChange: PropTypes.func,
  error: PropTypes.node,
  invalid: PropTypes.bool,
  label: PropTypes.string,
  descripcion: PropTypes.node,
  variant: PropTypes.oneOf(['publicar', 'administrar']),
  placeholder: PropTypes.string
}

export default InputEAN
